import { ElementRef, Injectable, Renderer2, RendererFactory2 } from '@angular/core';

import { BehaviorSubject, catchError, map, Observable, of, take } from 'rxjs';

import { SentryLogger } from '@fcom/core';
import { FinnairOrder } from '@fcom/dapi/api/models';
import { LanguageService } from '@fcom/ui-translate/services/language.service';

import { ExpediaLocaleData } from '../../interfaces';
import { localeToLanguageAndCurrency } from '../../constants';
import { ExpediaHotelsWidgetStatus } from '../../modules/common/enums';

@Injectable({
  providedIn: 'root',
})
export class ExpediaHotelsWidgetService {
  private readonly iFrameContainerSelector = '#krazyglue-embedded-wrapper';

  private renderer: Renderer2;
  private wrapperObserver?: MutationObserver;

  protected readonly WidgetStatus = ExpediaHotelsWidgetStatus;

  private readonly _iframeState$ = new BehaviorSubject<ExpediaHotelsWidgetStatus>(ExpediaHotelsWidgetStatus.INITIAL);
  readonly iframeState$ = this._iframeState$.asObservable();

  public readonly isSupportedLanguage$: Observable<boolean> = this.languageService.locale.pipe(
    map((locale) => locale in localeToLanguageAndCurrency)
  );

  constructor(
    private languageService: LanguageService,
    private sentryLogger: SentryLogger,
    rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  public initWidget(order: FinnairOrder, elementRef: ElementRef): void {
    const { localeValue: locale, langValue: lang } = this.languageService;
    const config = this.getExpediaConfig({
      locale,
      lang,
    });

    this._iframeState$.next(ExpediaHotelsWidgetStatus.LOADING);

    const requestUrl = this.buildRequestUrl({
      order,
      config,
      useProxy: true,
    });

    requestUrl.pipe(take(1)).subscribe((url) => {
      if (url) {
        this.createScriptTag(elementRef, url);
      }
    });
  }

  getExpediaConfig({ locale }: { lang: string; locale: string }): ExpediaLocaleData {
    return localeToLanguageAndCurrency[locale] ?? localeToLanguageAndCurrency['en_IE'];
  }

  private createScriptTag(elementRef: ElementRef, src: string): void {
    // Remove existing script tag if it exists
    const existingScript = elementRef.nativeElement.querySelector('script[src*="xsell-pwa"]');
    if (existingScript) {
      this.renderer.removeChild(elementRef.nativeElement, existingScript);
    }

    // Create new script tag
    const script = this.renderer.createElement('script');
    script.onload = () => this.watchWrapperForIframe(elementRef);
    script.onerror = () => this._iframeState$.next(ExpediaHotelsWidgetStatus.ERROR);

    this.renderer.setAttribute(script, 'src', src);
    this.renderer.setAttribute(script, 'type', 'text/javascript');
    this.renderer.setAttribute(script, 'crossorigin', 'anonymous');
    this.renderer.setAttribute(script, 'referrerpolicy', 'origin-when-cross-origin');

    // Append to the component's native element
    this.renderer.appendChild(elementRef.nativeElement, script);
  }

  private watchWrapperForIframe(elementRef: ElementRef): void {
    const wrapper: HTMLElement | null = elementRef.nativeElement.querySelector(this.iFrameContainerSelector);
    if (!wrapper) {
      return;
    }

    this.observeWrapperForIframe(wrapper);
  }

  private observeWrapperForIframe(wrapper: HTMLElement): void {
    this.wrapperObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        const iframe = Array.from(m.addedNodes).find((n) => n instanceof HTMLIFrameElement) as
          | HTMLIFrameElement
          | undefined;

        if (iframe?.src) {
          setTimeout(() => this.wrapperObserver?.disconnect(), 0);
          this.observeIframeInitialResizeReady(iframe).subscribe((isReady) => {
            if (isReady) {
              this._iframeState$.next(ExpediaHotelsWidgetStatus.LOADED);
            } else {
              this._iframeState$.next(ExpediaHotelsWidgetStatus.ERROR);
            }
          });
          break;
        }
      }
    });

    this.wrapperObserver.observe(wrapper, { childList: true, subtree: true });
  }

  /** Build final Expedia URL (signature & all parameters). */
  private buildRequestUrl({
    useProxy,
  }: {
    order: FinnairOrder;
    config: ExpediaLocaleData;
    useProxy: boolean;
  }): Observable<string> {
    const partnerId = 'finnair-finland-external-mmb-trips';
    const outboundEndDateTime = '2025-07-30T04:35:00';
    const returnStartDateTime = '2025-08-15T05:15:00';
    const destinationTla = 'ARN';
    const numOfAdults = '1';
    const locale = 'en_GB';
    const currencyCode = 'EUR';
    const bookingDateTime = '2025-07-11T08:03:10';
    const bookingStatus = 'confirmed';

    // Parameter list in Expedia ‑ required order with signature always at end
    const queryString =
      `partnerId=${encodeURIComponent(partnerId)}` +
      `&outboundEndDateTime=${encodeURIComponent(outboundEndDateTime)}` +
      `&returnStartDateTime=${encodeURIComponent(returnStartDateTime)}` +
      `&destinationTla=${encodeURIComponent(destinationTla)}` +
      `&numOfAdults=${encodeURIComponent(numOfAdults)}` +
      `&locale=${encodeURIComponent(locale)}` +
      `&currencyCode=${encodeURIComponent(currencyCode)}` +
      `&bookingDateTime=${encodeURIComponent(bookingDateTime)}` +
      `&bookingStatus=${encodeURIComponent(bookingStatus)}`;

    const requestStr = `/xsell-pwa.js?${queryString}`;
    const signature = 'KvrsI48NU5Al-5Zs5SbNY2OVb5Q';
    return of(signature).pipe(
      map((signature) => `${this.requestBaseUrl(useProxy)}${requestStr}&signature=${signature}`),
      catchError((err: unknown) => {
        console.error('Error building Expedia URL', err);
        return of('');
      })
    );
  }

  /**
   * Returns an Observable that emits true when the iframe is resized with height and width > 1 because only then it's ready to be shown to the users.
   * If not ready within 5 seconds, it will emit false.
   */
  private observeIframeInitialResizeReady(iframe: HTMLIFrameElement): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.width > 1 && entry.contentRect.height > 1) {
            resizeObserver.disconnect();
            observer.next(true);
            observer.complete();
            return;
          }
        }
      });

      resizeObserver.observe(iframe);

      const timeoutFiveSeconds = 5000;
      const timeoutId = setTimeout(() => {
        resizeObserver.disconnect();
        observer.next(false);
        observer.complete();
      }, timeoutFiveSeconds);

      return () => {
        clearTimeout(timeoutId);
        resizeObserver.disconnect();
      };
    }).pipe(
      take(1),
      catchError((error: unknown) => {
        this.sentryLogger.error('Error from Expedia Hotels iframe', error);
        return of(false);
      })
    );
  }

  private requestBaseUrl(useProxy: boolean): string {
    return useProxy ? `expediamod` : `https://xsell.expedia.com`;
  }
}
